/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {Header} from '@tanstack/react-table'
import UiTanstackTable from 'components/Ui/UiTanstackTable'
import UiTableColumnSearch from 'components/Ui/UiTableColumnSearch'
import UiTableColumnSelectFilter from 'components/Ui/UiTableColumnSelectFilter'
import TableFilterSellers from 'pages/Front/Booking/Seller/components/Table/Filters/TableFilterSellers'
import {Reservation} from 'types/entities/Reservation'

import {useTranslation} from 'react-i18next'
import UiDateRangePicker from 'components/Ui/UiDateRangePicker'
import {DateRange} from 'types/entities/DateRange'
import UiTableTimeRangeFilter from 'components/Ui/UiTableTimeRangeFilter'
import UiNumberRangeFilter from 'components/Ui/UiNumberRangeFilter'
import UiPriceRangeFilter from 'components/Ui/UiPriceRangeFilter'
import {useReservationStatusOptions} from 'utils/reservationUtils'
import {getPackageOptions} from 'utils/packageUtils'
import {getTableRangeFilterConfig} from 'constants/TableRangeFilterConfig'
import {ReservationStatus} from 'types/enums/ReservationStatus'
import {currencyNumberFormat} from 'utils/utility'
import {useBookingSellerReservationsContext} from './BookingSellerReservationsContext'
import {useCompanyBranchTypeOptions} from 'utils/companyUtils'

const BookingSellerReservationsTable = (props: { isOnlineOnly: boolean }) => {
    const { tableInit, isTableStateSame, onlineOnly, onRowClick } = useBookingSellerReservationsContext()
    const { t } = useTranslation()
    const { options: reservationStatusOptions } = useReservationStatusOptions(props.isOnlineOnly)
    const { options: accountTypeOptions } = useCompanyBranchTypeOptions()
    const packageOptions = getPackageOptions()
    const resetPage = () =>
        tableInit.setPagination({ pageIndex: 0, pageSize: tableInit.pageSize })
    const allPersonsCount = tableInit.table
        .getRowModel()
        .rows.reduce(
            (accumulator: number, row: { original: Reservation }) => accumulator + (row.original.persons || 0),
            0
        )

    const footerTotals = tableInit.table.getFilteredRowModel().rows.reduce(
        (
            accumulator: { totalPersons: number; totalRevenue: number; totalEffectiveRevenue: number },
            row: { original: Reservation }
        ) => {
            const reservation = row.original
            const price = Number(reservation?.price || 0)
            const total = Number(reservation?.total_invoice || 0)

            accumulator.totalPersons += reservation.persons || 0
            accumulator.totalRevenue += Number.isNaN(price) ? 0 : price
            accumulator.totalEffectiveRevenue += Number.isNaN(total) ? 0 : total

            return accumulator
        },
        { totalPersons: 0, totalRevenue: 0, totalEffectiveRevenue: 0 }
    )

    return (
        <UiTanstackTable
            className="BookingSellerReservationsTable min-h-0"
            tableInit={tableInit}
            isTableStateSame={isTableStateSame}
            onRowClick={(row) => onRowClick && onRowClick(row.original)}
            renderHeaderTitleProps={() => ({
                personsCount: allPersonsCount,
            })}
            renderFooter={() => (
                <div className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-4 py-3">
                    <div className="flex justify-end gap-8 text-sm font-semibold">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-600 dark:text-gray-400">{t('Total Persons')}:</span>
                            <span className="text-gray-900 dark:text-white">{footerTotals.totalPersons}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-600 dark:text-gray-400">
                                {t('Total Reserved Revenue')}:
                            </span>
                            <span className="text-gray-900 dark:text-white">
                                {currencyNumberFormat(footerTotals.totalRevenue)}
                            </span>
                        </div>
                        {!props.isOnlineOnly && (
                            <div className="flex items-center gap-2">
                                <span className="text-gray-600 dark:text-gray-400">
                                    {t('Total Effective Revenue')}:
                                </span>
                                <span className="text-gray-900 dark:text-white">
                                    {currencyNumberFormat(footerTotals.totalEffectiveRevenue)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
            renderHeaderFilter={(header: Header<Reservation, unknown>, tableInit: any) => {
                if (!header.column.getCanFilter()) {
                    return null
                }

                if (header.id === 'seller_id') {
                    return <TableFilterSellers column={header.column} table={tableInit.table} />
                }

                if (['created_at', 'updated_at', 'date'].includes(header.id)) {
                    const selectedDateRange = (() => {
                        const filterValue = header.column.getFilterValue() as
                            | DateRange
                            | [string, string]
                            | null
                            | undefined
                        if (!filterValue) return null
                        if (Array.isArray(filterValue)) {
                            if (filterValue.length !== 2) return null
                            return { from: String(filterValue[0]), to: String(filterValue[1]) }
                        }
                        if (typeof filterValue === 'object' && 'from' in filterValue && 'to' in filterValue) {
                            return {
                                from: String(filterValue.from),
                                to: String(filterValue.to),
                            }
                        }
                        return null
                    })()

                    return (
                        <div className="w-full">
                            <UiDateRangePicker
                                selectedDay={selectedDateRange}
                                onChange={(dates) => header.column.setFilterValue(dates)}
                                className="w-full"
                            />
                        </div>
                    )
                }

                if (header.id === 'time') {
                    const filterValue =
                        (header.column.getFilterValue() as {
                            from?: string
                            to?: string
                            start?: string
                            end?: string
                        }) || {}

                    const normalizedValue = {
                        from: filterValue.from || filterValue.start,
                        to: filterValue.to || filterValue.end,
                    }

                    return (
                        <div className="w-full">
                            <UiTableTimeRangeFilter
                                value={normalizedValue}
                                onChange={(value) => header.column.setFilterValue(value)}
                            />
                        </div>
                    )
                }

                if (header.id === 'status') {
                    if (onlineOnly) {
                        const reservedOnlyOption = reservationStatusOptions.filter(
                            (option) => option.value === ReservationStatus.Reserved
                        )

                        return (
                            <UiTableColumnSelectFilter
                                disabled
                                options={reservedOnlyOption}
                                value={[ReservationStatus.Reserved]}
                                showAllLabelWhenAllSelected={false}
                                onChange={() => header.column.setFilterValue([ReservationStatus.Reserved])}
                            />
                        )
                    }

                    return (
                        <UiTableColumnSelectFilter
                            options={reservationStatusOptions}
                            value={(header.column.getFilterValue() as string | string[]) ?? []}
                            onChange={(value) => header.column.setFilterValue(value.length > 0 ? value : undefined)}
                        />
                    )
                }

                if (header.id === 'package') {
                    return (
                        <UiTableColumnSelectFilter
                            options={packageOptions}
                            value={(header.column.getFilterValue() as string | string[]) ?? []}
                            onChange={(value) => header.column.setFilterValue(value.length > 0 ? value : undefined)}
                        />
                    )
                }

                if (header.id === 'company_type') {
                    return (
                        <UiTableColumnSelectFilter
                            options={accountTypeOptions}
                            value={(header.column.getFilterValue() as string | string[]) ?? []}
                            onChange={(value) => {
                                resetPage()
                                header.column.setFilterValue(value.length > 0 ? value : undefined)
                            }}
                        />
                    )
                }

                if (header.id === 'total' || header.id === 'total_invoice') {
                    const rangeFilterConfig = getTableRangeFilterConfig(header.id)

                    return (
                        <UiPriceRangeFilter
                            value={
                                (header.column.getFilterValue() as {
                                    from?: number
                                    to?: number
                                }) ?? {}
                            }
                            onChange={(value) => {
                                resetPage()
                                header.column.setFilterValue(value)
                            }}
                            maxValue={rangeFilterConfig?.maxValue}
                            maxDigits={rangeFilterConfig?.maxDigits}
                        />
                    )
                }

                if (header.id === 'persons') {
                    const rangeFilterConfig = getTableRangeFilterConfig('persons')

                    return (
                        <UiNumberRangeFilter
                            value={
                                (header.column.getFilterValue() as {
                                    from?: number
                                    to?: number
                                }) ?? {}
                            }
                            onChange={(value) => {
                                resetPage()
                                header.column.setFilterValue(value)
                            }}
                            placeholder={t('from - to')}
                            maxValue={rangeFilterConfig?.maxValue}
                            maxDigits={rangeFilterConfig?.maxDigits}
                        />
                    )
                }

                return (
                    <UiTableColumnSearch
                        value={(header.column.getFilterValue() as string) ?? ''}
                        onChange={(value) => header.column.setFilterValue(value)}
                        placeholder={t('Search')}
                    />
                )
            }}
        />
    )
}

export default BookingSellerReservationsTable
